import type { ReviewProjectAnalysisStatistics } from '@/api';
import type { SunburstData } from '@/utils/llmUsageStatistics';
import {
  childHueSpread,
  colorFromHue,
  lightnessForDepth,
  seededHue,
  spreadHue,
} from '@/utils/usageColors';

/** Finding severities, mildest first. Backend keeps unknown keys raw, so never rely on map order. */
export const SEVERITY_ORDER = ['low', 'medium', 'high'];

/** Neutral fallback for severities outside SEVERITY_ORDER. */
const UNKNOWN_SEVERITY_COLOR = 'hsl(0, 0%, 62%)';

/** Severity entries ordered low → high; unknown severities appended alphabetically. */
export function sortedSeverityEntries(
  counts: Record<string, number> | undefined
): [string, number][] {
  const rank = (severity: string): number => {
    const index = SEVERITY_ORDER.indexOf(severity);
    return index === -1 ? SEVERITY_ORDER.length : index;
  };
  return Object.entries(counts ?? {}).sort(
    (a, b) => rank(a[0]) - rank(b[0]) || a[0].localeCompare(b[0])
  );
}

function emptySunburst(): SunburstData {
  return { ids: [], labels: [], parents: [], values: [], colors: [] };
}

/**
 * Findings sunburst: ring 1 is one slice per analysis (hue seeded from its
 * playbook uid), ring 2 splits that analysis's findings by severity. Severity
 * colours are passed in (they come from the naive-ui theme, which is a
 * composable) so severity reads identically across every slice. Analyses
 * without findings are omitted — they still show in the runs table.
 */
export function buildFindingsSunburst(
  stats: ReviewProjectAnalysisStatistics,
  severityColors: Record<string, string>
): SunburstData {
  const data = emptySunburst();
  for (const [index, analysis] of (stats.analyses ?? []).entries()) {
    const entries = sortedSeverityEntries(analysis.finding_count_by_severity).filter(
      ([, count]) => count > 0
    );
    if (!entries.length) continue;
    // branchvalues 'total': the parent value must equal the sum of emitted leaves.
    const total = entries.reduce((sum, [, count]) => sum + count, 0);
    const rootId = analysis.analysis_uid ?? `${analysis.playbook_uid}#${index}`;
    data.ids.push(rootId);
    data.labels.push(analysis.analysis_name ?? analysis.playbook_name);
    data.parents.push('');
    data.values.push(total);
    data.colors.push(
      colorFromHue(seededHue(analysis.playbook_uid), { lightness: lightnessForDepth(0) })
    );
    for (const [severity, count] of entries) {
      data.ids.push(`${rootId} › ${severity}`);
      data.labels.push(severity.charAt(0).toUpperCase() + severity.slice(1));
      data.parents.push(rootId);
      data.values.push(count);
      data.colors.push(severityColors[severity] ?? UNKNOWN_SEVERITY_COLOR);
    }
  }
  return data;
}

/**
 * File-type sunburst derived from the mime string itself: ring 1 is the major
 * type (application, image, …), leaves are the subtypes (pdf, png, …).
 * `count_by_file_category` can't serve as the outer ring — it is an independent
 * flat map with no joint distribution against mime types.
 */
export function buildMimeTypeSunburst(
  countByMimeType: Record<string, number> | undefined
): SunburstData {
  const groups = new Map<string, { label: string; count: number }[]>();
  for (const [mime, count] of Object.entries(countByMimeType ?? {})) {
    if (count <= 0) continue;
    const slash = mime.indexOf('/');
    const major = slash === -1 ? mime : mime.slice(0, slash);
    const label = slash === -1 ? mime : mime.slice(slash + 1);
    const leaves = groups.get(major);
    if (leaves) leaves.push({ label, count });
    else groups.set(major, [{ label, count }]);
  }

  const data = emptySunburst();
  for (const [major, leaves] of groups) {
    const hue = seededHue(major);
    data.ids.push(major);
    data.labels.push(major);
    data.parents.push('');
    data.values.push(leaves.reduce((sum, leaf) => sum + leaf.count, 0));
    data.colors.push(colorFromHue(hue, { lightness: lightnessForDepth(0) }));
    for (const [index, leaf] of leaves
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
      .entries()) {
      data.ids.push(`${major}/${leaf.label}`);
      data.labels.push(leaf.label);
      data.parents.push(major);
      data.values.push(leaf.count);
      data.colors.push(
        colorFromHue(spreadHue(hue, childHueSpread(1), index, leaves.length), {
          lightness: lightnessForDepth(1),
        })
      );
    }
  }
  return data;
}
